import { useEffect, useRef, useState } from 'react';
import { history, useLocation } from 'umi';
import { useMemoizedFn } from 'ahooks';

import useClientPaginationAndOrder from '@intelligence/hooks/useClientPaginationAndOrder';
import useCancelTokenRequest from '@intelligence/hooks/useCancelTokenRequest';
import { getTrueDataFeedbackHistoryNew } from '@intelligence/services/topcharts';

import { buildTrueDataComparator } from '../utils';
import {
  normalizeAlineaTrueDataRows,
  normalizeDataBrainTrueDataRows,
} from '../utils/trueData';

import type { IPublicFeedback } from '@intelligence/types/topCharts';
import type { IPublicFeedbackParams, TSourceAvailabilityStatus, TTrueDataMode } from '../types';
import type { AxiosRequestConfig } from 'axios';

const PAGE_SIZE = 100;

interface IUseTrueDataFeedbackDataParams {
  gameId?: string;
  feedBackParams?: IPublicFeedbackParams;
}

interface ISourceDataResult {
  dataList: IPublicFeedback[];
  total: number;
}

interface IUseTrueDataFeedbackDataReturn {
  sourceAvailability: Record<TTrueDataMode, TSourceAvailabilityStatus>;
  hasAlineaDrilldownContext: boolean;
  trueDataMode: TTrueDataMode;
  internalTableFeedBackData: IPublicFeedback[];
  internalTableFeedBackDataTotal: number;
  internalIsTableFeedBackDataLoading: boolean;
  setTrueDataMode: React.Dispatch<React.SetStateAction<TTrueDataMode>>;
  reloadSources: () => void;
  resetData: () => void;
  fetchTrueDataFeedbackHistory: (params: IPublicFeedbackParams, mode?: TTrueDataMode) => Promise<void>;
}

type ITrueDataHistoryService = (params: IRecord) => Promise<IRecord>;

const getDataBrainHistory = async (params: IRecord, config?: AxiosRequestConfig): Promise<IRecord> => {
  const response = await getTrueDataFeedbackHistoryNew({ ...params, mode: 'di' }, config);
  const responseData = response?.data || {};
  const histories = Array.isArray(responseData.history) ? responseData.history : [];

  return {
    ...response,
    data: {
      ...responseData,
      history: normalizeDataBrainTrueDataRows(histories),
    },
  };
};

const getAlineaHistory = async (params: IRecord, config?: AxiosRequestConfig): Promise<IRecord> => {
  const response = await getTrueDataFeedbackHistoryNew({ ...params, mode: 'alinea' }, config);
  const responseData = response?.data || {};
  const histories = Array.isArray(responseData.history) ? responseData.history : [];

  return {
    ...response,
    data: {
      ...responseData,
      history: normalizeAlineaTrueDataRows(histories),
    },
  };
};

export default function useTrueDataFeedbackData(
  params: IUseTrueDataFeedbackDataParams,
): IUseTrueDataFeedbackDataReturn {
  const { gameId, feedBackParams } = params;
  const location = useLocation();
  const hasAlineaDrilldownContext = new URLSearchParams(location.search).get('openTrueData') === 'alinea';

  const { service: getDataBrainHistoryWithCancel } = useCancelTokenRequest(getDataBrainHistory);
  const { service: getAlineaHistoryWithCancel } = useCancelTokenRequest(getAlineaHistory);

  const enhancedService = useClientPaginationAndOrder(getDataBrainHistoryWithCancel, {
    paginationFieldsMapping: {
      listField: 'history',
      totalField: 'count',
    },
    rewriteFields: [],
    buildComparator: (orderInfo) => buildTrueDataComparator('di', orderInfo),
  });
  const alineaEnhancedService = useClientPaginationAndOrder(getAlineaHistoryWithCancel, {
    paginationFieldsMapping: {
      listField: 'history',
      totalField: 'count',
    },
    rewriteFields: [],
    buildComparator: (orderInfo) => buildTrueDataComparator('alinea', orderInfo),
  });

  const [sourceAvailability, setSourceAvailability] = useState<
  Record<TTrueDataMode, TSourceAvailabilityStatus>
  >({ di: 'unknown', alinea: 'unknown' });
  const [sourceReloadKey, setSourceReloadKey] = useState(0);
  const [internalTableFeedBackData, setInternalTableFeedBackData] = useState<IPublicFeedback[]>([]);
  const [internalTableFeedBackDataTotal, setInternalTableFeedBackDataTotal] = useState(0);
  const [internalIsTableFeedBackDataLoading, setInternalIsTableFeedBackDataLoading] = useState(false);
  const [trueDataMode, setTrueDataMode] = useState<TTrueDataMode>('di');
  /** 游戏、来源或弹窗状态切换后，避免旧请求覆盖当前表格。 */
  const trueDataRequestIdRef = useRef(0);

  // 来源可用性只依赖查询身份；分页和排序变化由当前表格请求处理，不重复探测来源。
  const sourceAvailabilityParamsKey = JSON.stringify({
    entity_type: feedBackParams?.entity_type,
    game_id: feedBackParams?.game_id || gameId,
    page_id: feedBackParams?.page_id,
  });

  useEffect(() => {
    // 查询身份变化后，旧来源状态不再代表当前游戏，下一次探测前不允许下载旧数据。
    setSourceAvailability({ di: 'unknown', alinea: 'unknown' });
  }, [sourceAvailabilityParamsKey]);

  const fetchTrueDataFeedbackHistory = useMemoizedFn(
    async (requestParams: IPublicFeedbackParams, mode = trueDataMode): Promise<void> => {
      trueDataRequestIdRef.current += 1;
      const requestId = trueDataRequestIdRef.current;
      setInternalIsTableFeedBackDataLoading(true);

      try {
        const service = mode === 'di' ? enhancedService : alineaEnhancedService;
        const response = await service(requestParams);
        const dataList = Array.isArray(response?.data?.history) ? response.data.history : [];
        const total = response?.data?.count ?? dataList.length;

        if (requestId !== trueDataRequestIdRef.current) {
          return;
        }

        setInternalTableFeedBackDataTotal(total);
        setInternalTableFeedBackData([...dataList]);
        setSourceAvailability((previous) => ({
          ...previous,
          [mode]: total > 0 ? 'available' : 'empty',
        }));
      } catch (error) {
        console.error('Failed to fetch true data feedback history:', error);
        if (requestId !== trueDataRequestIdRef.current) {
          return;
        }

        setInternalTableFeedBackDataTotal(0);
        setInternalTableFeedBackData([]);
        setSourceAvailability((previous) => ({ ...previous, [mode]: 'empty' }));
      } finally {
        if (requestId === trueDataRequestIdRef.current) {
          setInternalIsTableFeedBackDataLoading(false);
        }
      }
    },
  );

  useEffect(() => {
    let isUnmounted = false;
    trueDataRequestIdRef.current += 1;
    const requestId = trueDataRequestIdRef.current;

    const fetchSourceData = async (
      service: ITrueDataHistoryService,
      requestParams: IRecord,
    ): Promise<ISourceDataResult> => {
      try {
        const response = await service(requestParams);
        const dataList = Array.isArray(response?.data?.history) ? response.data.history : [];
        return {
          dataList,
          total: response?.data?.count ?? dataList.length,
        };
      } catch (error) {
        // 预探测失败时不开放下载入口，保持现有的安全降级行为。
        return { dataList: [], total: 0 };
      }
    };

    const fetchAllSourceData = async (): Promise<void> => {
      if (!hasAlineaDrilldownContext && sourceReloadKey === 0) {
        return;
      }

      setInternalIsTableFeedBackDataLoading(true);
      setSourceAvailability({ di: 'unknown', alinea: 'unknown' });

      const [diResult, alineaResult] = await Promise.all([
        fetchSourceData(enhancedService, {
          ...feedBackParams,
          order_metric: 'metric_date',
          order: 'desc',
          page: 1,
          page_size: PAGE_SIZE,
        }),
        fetchSourceData(alineaEnhancedService, {
          ...feedBackParams,
          order_metric: 'date',
          order: 'desc',
          page: 1,
          page_size: PAGE_SIZE,
        }),
      ]);

      if (isUnmounted || requestId !== trueDataRequestIdRef.current) {
        return;
      }

      let nextMode: TTrueDataMode = 'di';
      if (hasAlineaDrilldownContext) {
        nextMode = alineaResult.total > 0 ? 'alinea' : 'di';
      } else if (!diResult.total && alineaResult.total) {
        nextMode = 'alinea';
      }
      const activeResult = nextMode === 'alinea' ? alineaResult : diResult;

      setTrueDataMode(nextMode);
      setSourceAvailability({
        di: diResult.total > 0 ? 'available' : 'empty',
        alinea: alineaResult.total > 0 ? 'available' : 'empty',
      });
      setInternalTableFeedBackDataTotal(activeResult.total);
      setInternalTableFeedBackData([...activeResult.dataList]);
      setInternalIsTableFeedBackDataLoading(false);

      if (hasAlineaDrilldownContext) {
        const searchParams = new URLSearchParams(location.search);
        searchParams.delete('openTrueData');
        history.replace({
          pathname: location.pathname,
          search: searchParams.toString() ? `?${searchParams.toString()}` : '',
          hash: location.hash,
        });
      }
    };

    void fetchAllSourceData();
    return () => {
      isUnmounted = true;
    };
  }, [gameId, hasAlineaDrilldownContext, sourceAvailabilityParamsKey, sourceReloadKey]);

  const reloadSources = useMemoizedFn(() => {
    setInternalIsTableFeedBackDataLoading(true);
    setSourceReloadKey((value) => value + 1);
  });

  const resetData = useMemoizedFn(() => {
    trueDataRequestIdRef.current += 1;
    setInternalTableFeedBackDataTotal(0);
    setInternalTableFeedBackData([]);
    setInternalIsTableFeedBackDataLoading(false);
  });

  return {
    sourceAvailability,
    hasAlineaDrilldownContext,
    trueDataMode,
    internalTableFeedBackData,
    internalTableFeedBackDataTotal,
    internalIsTableFeedBackDataLoading,
    setTrueDataMode,
    reloadSources,
    resetData,
    fetchTrueDataFeedbackHistory,
  };
}
